import { File } from '@agora/icons'
import {
  BoldOutlined,
  CheckSquareOutlined,
  HighlightOutlined,
  ItalicOutlined,
  OrderedListOutlined,
  StrikethroughOutlined,
  UnorderedListOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { EditorOptions, Editor as TiptapEditor } from '@tiptap/core'
import { EditorContent } from '@tiptap/react'
import { useUpload } from '@web/react-common'
import { useDebounceFn, useInViewport } from 'ahooks'
import { Button, Input, Popconfirm, Tooltip, Typography } from 'antd'
import axios from 'axios'
import cls from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { getEditor } from './editor'
import s from './editor.module.less'
import { MentionUser } from './mention-list.component'

export interface EditorProps extends Partial<EditorOptions> {
  className?: string
  content?: string
  editable?: boolean
  canRead?: boolean
  isFocus?: boolean
  editorKey?: string
  placeholder?: string
  disableAutoSaveStyle?: boolean
  textMaxCount?: number
  onChange?: (content: string) => void
  onSave?: (content: string) => Promise<void>
  viewerEmail?: string
  users?: MentionUser[]
  menu?: {
    disableHeading?: boolean
    disableUpload?: boolean
    pmoReportButton?: {
      onClick: () => void
    }
    hasCheckButton?: boolean
    onCancel?: () => void
  }
}

const MosaicComponent: React.FC<{ content?: any }> = ({ content }) => {
  content = content ?? '********'
  const head = content.split('*').filter(Boolean)[0]
  const last = content.split('*').filter(Boolean)[1]
  const hideLength = content.match(/\*/g) ? content.match(/\*/g)!.length : 0
  return (
    <span className='break-all'>
      <span>{head}</span>
      <span className='text-transparent text-shadow-lg'>{'*'.repeat(hideLength)}</span>
      <span>{last}</span>
    </span>
  )
}

export const Editor: React.FC<EditorProps> = ({
  className,
  editorKey = 'tiptap-editor',
  content = '',
  editable = true,
  canRead = true,
  textMaxCount,
  onChange,
  onSave,
  disableAutoSaveStyle,
  viewerEmail = '',
  users = [],
  menu = {},
  isFocus = false,
  ...props
}) => {
  const { t } = useTranslation()
  const rootRef = React.useRef<HTMLDivElement>(null)
  const [status, setStatus] = React.useState<string>('')
  const [editor, setEditor] = React.useState<TiptapEditor | null>(null)

  const { uploadClickRef } = useUpload(
    {
      upload: async (file) => {
        const { data } = await axios({
          url: '/api/file/getOSSMeta',
          params: file,
        })
        return data
      },
      onUploadStart(_file) {},
      onUploadProgress(_file) {},
      onUploadComplete(file) {
        if (editor) {
          editor.chain().focus().setImage({ src: file.url }).run()
        }
      },
      onUploadError(_file) {},
      clickOptions: {
        accept: 'image/*',
      },
    },
    [editor, isFocus]
  )

  const inViewPort = useInViewport(rootRef)

  const { run: debouncedOnSaveContent } = useDebounceFn(
    async (content) => {
      if (!onSave) {
        return
      }
      if (!editable) {
        setStatus('没有保存权限')
        return
      }
      if (onSave)
        try {
          await onSave(content)
          setStatus('保存成功')
        } catch {
          setStatus('保存失败')
        }
    },
    {
      leading: false,
      wait: 1200,
    }
  )

  React.useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy()
      }
    }
  }, [])

  React.useEffect(() => {
    if (editor) {
      editor.commands.setContent(content)
    }
  }, [editorKey])

  // update
  // BUG: cursor 在每次更新后都会移动到末尾， https://github.com/ueberdosis/tiptap/issues/2216
  React.useEffect(() => {
    if (editor) {
      const { from, to } = editor.state.selection
      editor.commands.setContent(content, undefined, { preserveWhitespace: 'full' })
      editor.commands.setTextSelection({ from, to })
    }
  }, [content])

  React.useEffect(() => {
    if (editor && isFocus) {
      editor.commands.focus()
    }
  }, [isFocus, editor])

  React.useEffect(() => {
    if (editor) {
      editor.setEditable(editable)
    }
  }, [editable])

  React.useEffect(() => {
    if (!inViewPort) {
      return
    }
    if (editor) {
      return
    }
    setEditor(
      getEditor({
        ...props,
        editable,
        users,
        content,
        textMaxCount,
        onUpdate({ editor }) {
          const html = editor.getHTML()
          setStatus('')
          if (onSave && !menu.hasCheckButton) {
            debouncedOnSaveContent(html)
          }
          if (onChange) {
            onChange(html)
          }
        },
      })
    )
  }, [inViewPort])

  const controls = editor && (
    <div className={s.controls}>
      <Typography.Text
        className={cls({
          [s.controlsSaved]: status === '保存成功',
          'opacity-100': status === '保存失败',
        })}
        type={{ 保存成功: 'default', 保存失败: 'danger' }[status] as any}
      >
        {status}
      </Typography.Text>
    </div>
  )

  const isChrome = navigator.userAgent.indexOf('Chrome') !== -1

  const confirmCancel = () => {
    menu && menu.onCancel && menu.onCancel()
  }

  const menus = editable && editor && isFocus && (
    <div className={`${s.menus} editor-menu`}>
      <div>
        {isChrome && (
          <Input
            type='color'
            style={{ width: '24px', height: '24px', border: 0, padding: 0, marginRight: '7px' }}
            onInput={(e) => editor.chain().focus().setColor(e.target['value']).run()}
            value={editor.getAttributes('textStyle').color}
          ></Input>
        )}
        <a
          onClick={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleBold().run()
          }}
          tabIndex={0}
          className={editor.isActive('bold') ? `${s.active} ${s['editor-menu-item']}` : `${s['editor-menu-item']}`}
        >
          <BoldOutlined />
        </a>
        <a
          onClick={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleItalic().run()
          }}
          tabIndex={0}
          className={editor.isActive('italic') ? `${s.active} ${s['editor-menu-item']}` : `${s['editor-menu-item']}`}
        >
          <ItalicOutlined />
        </a>
        <a
          onClick={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleStrike().run()
          }}
          tabIndex={0}
          className={editor.isActive('strike') ? `${s.active} ${s['editor-menu-item']}` : `${s['editor-menu-item']}`}
        >
          <StrikethroughOutlined />
        </a>
        <a
          onClick={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleHighlight().run()
          }}
          tabIndex={0}
          className={editor.isActive('highlight') ? `${s.active} ${s['editor-menu-item']}` : `${s['editor-menu-item']}`}
        >
          <HighlightOutlined />
        </a>
        {!menu.disableHeading && (
          <a
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }}
            tabIndex={0}
            className={
              editor.isActive('heading', { level: 1 })
                ? `${s.active} ${s['editor-menu-item']}`
                : `${s['editor-menu-item']}`
            }
          >
            H1
          </a>
        )}
        {!menu.disableHeading && (
          <a
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }}
            tabIndex={0}
            className={
              editor.isActive('heading', { level: 2 })
                ? `${s.active} ${s['editor-menu-item']}`
                : `${s['editor-menu-item']}`
            }
          >
            H2
          </a>
        )}
        <a
          onClick={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleOrderedList().run()
          }}
          tabIndex={0}
          className={
            editor.isActive('orderedList') ? `${s.active} ${s['editor-menu-item']}` : `${s['editor-menu-item']}`
          }
        >
          <OrderedListOutlined />
        </a>
        <a
          onClick={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleBulletList().run()
          }}
          tabIndex={0}
          className={
            editor.isActive('bulletList') ? `${s.active} ${s['editor-menu-item']}` : `${s['editor-menu-item']}`
          }
        >
          <UnorderedListOutlined />
        </a>
        <a
          onClick={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleTaskList().run()
          }}
          tabIndex={0}
          className={editor.isActive('taskList') ? `${s.active} ${s['editor-menu-item']}` : `${s['editor-menu-item']}`}
        >
          <CheckSquareOutlined />
        </a>
        {!menu.disableUpload && isChrome && (
          <a tabIndex={0} className={s['editor-menu-item']}>
            <UploadOutlined ref={uploadClickRef} />
          </a>
        )}
        {menu.pmoReportButton && (
          <Tooltip title={t('tiptap.menu.report')}>
            <a
              onClick={(e) => {
                e.preventDefault()
                menu.pmoReportButton?.onClick()
              }}
              tabIndex={0}
              className={s['editor-menu-item']}
              style={{ marginBottom: -2 }}
            >
              <File size={14} style={{ marginBottom: -2 }} />
            </a>
          </Tooltip>
        )}
      </div>
      {menu.hasCheckButton && (
        <div>
          <Popconfirm title={t('确认取消？')} onConfirm={confirmCancel} okText='Yes' cancelText='No'>
            <Button size='small' type='text' disabled={!editable}>
              {t('cancel')}
            </Button>
          </Popconfirm>
          <Button
            size='small'
            type='primary'
            onClick={() => debouncedOnSaveContent(editor.getHTML())}
            disabled={!editable}
          >
            {t('submit')}
          </Button>
        </div>
      )}
    </div>
  )

  return (
    <>
      <div
        ref={rootRef}
        className={cls(s.container, className, {
          [s.autoSave]: !!onSave && !disableAutoSaveStyle,
          [s.saved]: status === '保存成功' && !disableAutoSaveStyle,
        })}
        onClick={() => {
          if (editor && !editor.isFocused) {
            editor.commands.focus()
          }
        }}
      >
        {menus}
        {canRead ? (
          <EditorContent editor={editor as any}>
            {!disableAutoSaveStyle && controls}
            <style
              dangerouslySetInnerHTML={{
                __html: `
          .mention[data-id='${viewerEmail}'] {
            color: #099dfd !important;
          }
          `,
              }}
            />
          </EditorContent>
        ) : (
          <MosaicComponent />
        )}
      </div>

      {textMaxCount ? (
        <span
          style={{
            float: 'right',
            color: 'rgb(0,0,0,0.45)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {editor?.getText().length ?? 0}/{textMaxCount}
        </span>
      ) : null}
    </>
  )
}
